import type { Clip, Moment, MomentKind, Segment } from "./clip.js";
import { sourceRangesToTimeline } from "./quran.js";

/** Timeline ms of a source moment, or null when it falls in a cut. */
export function sourcePointToTimeline(segments: Segment[], ms: number): number | null {
  let acc = 0;
  for (const s of segments) {
    if (ms >= s.inMs && ms < s.outMs) return acc + (ms - s.inMs);
    acc += s.outMs - s.inMs;
  }
  return null;
}

export const WINDOW_KINDS: MomentKind[] = ["push", "sparkle", "ring", "leak", "quiet"];
export const isWindow = (m: Moment) => WINDOW_KINDS.includes(m.kind);

export interface TimelineMoment { moment: Moment; atMs: number; endMs: number | null; ranges: Segment[] }

/** Every moment placed on the timeline: instants get a point, windows get their kept ranges. Moments inside cuts are dropped. */
export function momentsOnTimeline(clip: Pick<Clip, "segments" | "direction">): TimelineMoment[] {
  const out: TimelineMoment[] = [];
  for (const m of clip.direction.moments) {
    if (isWindow(m)) {
      const ranges = sourceRangesToTimeline(clip.segments, [{ inMs: m.atMs, outMs: m.endMs ?? m.atMs + 4000 }]);
      if (ranges.length) out.push({ moment: m, atMs: ranges[0].inMs, endMs: ranges[ranges.length - 1].outMs, ranges });
    } else {
      const at = sourcePointToTimeline(clip.segments, m.atMs);
      if (at != null) out.push({ moment: m, atMs: at, endMs: null, ranges: [{ inMs: at, outMs: at }] });
    }
  }
  return out.sort((a, b) => a.atMs - b.atMs);
}

/** 0..1 strength of a windowed kind at a source moment, easing in and out over rampMs. */
export function windowStrength(clip: Pick<Clip, "direction">, kind: MomentKind, sourceMs: number, rampMs = 400): number {
  let best = 0;
  for (const m of clip.direction.moments) {
    if (m.kind !== kind) continue;
    const a = m.atMs, b = m.endMs ?? m.atMs + 4000;
    if (sourceMs < a - rampMs || sourceMs > b + rampMs) continue;
    const inn = Math.min(1, Math.max(0, (sourceMs - a + rampMs) / rampMs));
    const out = Math.min(1, Math.max(0, (b + rampMs - sourceMs) / rampMs));
    best = Math.max(best, Math.min(inn, out) * m.intensity);
  }
  return best;
}

/** Milliseconds since the nearest instant moment of a kind (0 at the moment), or -1 when none is within `withinMs` after it. */
export function sinceMoment(clip: Pick<Clip, "direction">, kind: MomentKind, sourceMs: number, withinMs = 600): { sinceMs: number; intensity: number } {
  let best = { sinceMs: -1, intensity: 0 };
  for (const m of clip.direction.moments) {
    if (m.kind !== kind) continue;
    const d = sourceMs - m.atMs;
    if (d >= 0 && d <= withinMs && (best.sinceMs < 0 || d < best.sinceMs)) best = { sinceMs: d, intensity: m.intensity };
  }
  return best;
}

/** Punch moments become crop runs like manual zoom marks. */
export const punchRuns = (clip: Pick<Clip, "direction">) => clip.direction.moments.filter((m) => m.kind === "punch").map((m) => ({ startMs: m.atMs, endMs: m.atMs + 600, manual: true }));

/** A stable number in [0, 1) for a clip and a slot: the small variations that keep clips from looking alike. */
export function vary(seed: number, slot: number): number {
  let x = (seed ^ (slot * 0x9e3779b9)) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  x = (x ^ (x >>> 16)) >>> 0;
  return x / 4294967296;
}
export function clipSeed(clip: Pick<Clip, "id" | "videoId" | "direction">): number {
  if (clip.direction.seed) return clip.direction.seed;
  let h = 2166136261;
  for (const ch of `${clip.videoId}/${clip.id}`) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619) >>> 0; }
  return h >>> 0;
}
export const momentId = () => `mo-${Date.now().toString(36)}${Math.floor(Math.random() * 1296).toString(36).padStart(2, "0")}`;
